import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import type { IExamReport } from "@/apis/exam_papers/getExamReport";
import { useNavigate } from "react-router-dom";

interface UserExamResultsTableProps {
  examResults: IExamReport[];
  isLoading: boolean;
  error: Error | null;
}

const UserExamResultsTable: React.FC<UserExamResultsTableProps> = ({
  examResults,
  isLoading,
  error,
}) => {
  const navigate = useNavigate();

  // Navigate to exam result detail page
  const handleViewResult = (resultId: string) => {
    navigate(`/exam_results/${resultId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatScore = (score?: number | null) => {
    if (score === undefined || score === null) return "-";
    return score.toString();
  };

  const formatSubjectScore = (score?: number | null) => {
    if (score === undefined || score === null) return "0";
    return score.toString();
  };

  const getSubjectScore = (
    subjects: IExamReport["subjects"],
    subjectType: string
  ) => {
    const subject = subjects?.find((s) => s.subject === subjectType);
    return subject?.total_score;
  };

  const formatTotalScoreWithDetails = (result: IExamReport) => {
    const totalScore = formatScore(result.total_score);

    if (!result.subjects || result.subjects.length === 0) {
      return <div className="font-medium text-gray-900">{totalScore}</div>;
    }

    const rwScore = getSubjectScore(result.subjects, "reading_writing");
    const mathScore = getSubjectScore(result.subjects, "math");

    return (
      <div>
        <div className="font-medium text-gray-900">{totalScore}</div>
        <div className="text-xs text-gray-500 mt-1">
          R&W: {formatSubjectScore(rwScore)}, Math:{" "}
          {formatSubjectScore(mathScore)}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading exam results...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        Error occurred: {error.toString()}
      </div>
    );
  }

  if (examResults.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        No exam results found for this student.
      </div>
    );
  }

  return (
    <Table className="mt-4">
      <TableHead>
        <TableRow>
          <TableHeader style={{ paddingLeft: "2rem", paddingRight: "1.5rem" }}>
            Exam Set
          </TableHeader>
          <TableHeader>Score</TableHeader>
          <TableHeader style={{ paddingLeft: "1.5rem", paddingRight: "2rem" }}>
            Completed At
          </TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {examResults.map((result) => (
          <TableRow
            key={result.id}
            className="cursor-pointer hover:bg-gray-50"
            onClick={() => handleViewResult(result.id)}
          >
            <TableCell style={{ paddingLeft: "2rem", paddingRight: "1.5rem" }}>
              <div className="font-medium">{result.exam_set?.title || "-"}</div>
              <div className="text-xs text-zinc-500">{result.exam_type}</div>
            </TableCell>
            <TableCell>{formatTotalScoreWithDetails(result)}</TableCell>
            <TableCell
              className="text-zinc-500"
              style={{ paddingLeft: "1.5rem", paddingRight: "2rem" }}
            >
              {formatDate(result.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default UserExamResultsTable;
